import { Link } from "@tanstack/react-router";
import { ArrowRight, MessageSquare, Trash2 } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";

interface Thread {
  _id: string;
  title: string;
  lastMessageAt: number;
  metadata?: { templateName?: string };
}

interface RecentThreadsProps {
  threads: Thread[];
  onDelete?: (args: { threadId: string }) => void;
}

function formatRelativeTime({ timestamp }: { timestamp: number }): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function RecentThreads({ threads, onDelete }: RecentThreadsProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  function handleDelete(threadId: string) {
    if (onDelete) {
      onDelete({ threadId });
    }
    setConfirmDeleteId(null);
  }

  if (threads.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-black/10 bg-white py-20 text-center dark:border-white/10 dark:bg-white/5 dark:backdrop-blur-xl">
        <p className="font-medium text-black/40 dark:text-white/40">
          No conversations yet. Start a new chat to get going.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {threads.map((thread, index) => (
        <motion.div
          key={thread._id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
        >
          {confirmDeleteId === thread._id ? (
            <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900/30 dark:bg-red-950/30">
              <span className="flex-1 text-sm font-medium text-red-600 dark:text-red-400">Delete this conversation?</span>
              <button
                onClick={() => handleDelete(thread._id)}
                className="rounded-lg px-3 py-1 text-xs font-bold text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                Yes
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="rounded-lg px-3 py-1 text-xs font-bold text-black/40 hover:bg-black/5 dark:text-white/40 dark:hover:bg-white/5"
              >
                No
              </button>
            </div>
          ) : (
            <Link
              to="/chat/$threadId"
              params={{ threadId: thread._id }}
              className="group flex items-center justify-between rounded-2xl border border-black/10 bg-white p-5 transition-all hover:border-brand-coral dark:border-white/10 dark:bg-white/5 dark:backdrop-blur-xl dark:hover:border-brand-coral"
            >
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-black/5 transition-colors group-hover:bg-brand-coral/10 dark:bg-white/5">
                  <MessageSquare className="h-6 w-6 text-black/40 transition-colors group-hover:text-brand-coral dark:text-white/40" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate font-bold text-black dark:text-white">{thread.title}</h3>
                  <div className="mt-1 flex items-center gap-3">
                    {thread.metadata?.templateName && (
                      <>
                        <span className="text-[10px] font-black uppercase tracking-widest text-brand-coral">
                          {thread.metadata.templateName}
                        </span>
                        <span className="text-black/20 dark:text-white/20">&bull;</span>
                      </>
                    )}
                    <span className="text-[11px] text-black/40 dark:text-white/40">
                      {formatRelativeTime({ timestamp: thread.lastMessageAt })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setConfirmDeleteId(thread._id);
                  }}
                  className="rounded-lg p-2 text-black/0 transition-colors hover:bg-black/5 group-hover:text-black/20 group-hover:hover:text-red-500 dark:text-white/0 dark:hover:bg-white/5 dark:group-hover:text-white/20 dark:group-hover:hover:text-red-400"
                  aria-label="Delete thread"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/5 transition-all group-hover:bg-brand-coral group-hover:text-white dark:bg-white/5">
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          )}
        </motion.div>
      ))}
    </div>
  );
}
